import React, { useState, ReactNode, useRef, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    IconButton,
    CircularProgress,
    Box,
    ClickAwayListener
} from '@mui/material';
import { MoreVert, Refresh, WebAsset, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface MenuAction {
    text: string;
    icon: ReactNode;
    onClick: () => void;
}

interface BaseWidgetProps {
    heading?: string;
    icon?: ReactNode;
    loading?: boolean;
    onRefresh?: () => void;
    onFullscreen?: (isFullscreen: boolean) => void;
    isFullscreen?: boolean;
    customActions?: Array<MenuAction>;
    children: ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

export const BaseWidget: React.FC<BaseWidgetProps> = ({
    heading,
    icon,
    loading = false,
    onRefresh,
    onFullscreen,
    isFullscreen = false,
    customActions,
    children,
    className = '',
    style
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [internalIsFullscreen, setInternalIsFullscreen] = useState(false);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = document.fullscreenElement === cardRef.current;
            setInternalIsFullscreen(isCurrentlyFullscreen);
            onFullscreen?.(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [onFullscreen]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRefresh = () => {
        onRefresh?.();
        handleMenuClose();
    };

    const handleMenuItemClick = (action: () => void) => {
        action();
        handleMenuClose();
    };

    const handleFullscreenToggle = async () => {
        if (!cardRef.current) return;

        try {
            if (!document.fullscreenElement) {
                await cardRef.current.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Error toggling fullscreen:', error);
        }
    };

    const isDark = document.body.hasAttribute('data-theme');

    return (
        <Card
            ref={cardRef}
            className={`app-card dashboard ${className}`}
            sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '0px',
                boxShadow: '0 1px 15px 1px rgba(69, 65, 78, 0.08)',
                transition: 'all 0.4s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 25px 2px rgba(69, 65, 78, 0.15)'
                },
                ...style
            }}
        >
            {heading && (
                <CardHeader
                    className="app-card-title"
                    avatar={
                        icon && (
                            <Box className="app-card-icon" sx={{ fontSize: '2rem', marginRight: '0.5rem' }}>
                                {icon}
                            </Box>
                        )
                    }
                    title={
                        <Typography
                            variant="h6"
                            component="h3"
                            className="app-title"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--gray-dark)'
                            }}
                        >
                            {heading}
                        </Typography>
                    }
                    action={
                        (onRefresh || onFullscreen || customActions?.length) && (
                            <Box display="flex" alignItems="center" className="app-contextual-link">
                                {onFullscreen && (
                                    <IconButton
                                        onClick={handleFullscreenToggle}
                                        size="small"
                                        sx={{ margin: 0, marginTop: 2, color: 'grey.500' }}
                                    >
                                        {internalIsFullscreen ? <Remove /> : <WebAsset />}
                                    </IconButton>
                                )}
                                <ClickAwayListener onClickAway={handleMenuClose}>
                                    <Box sx={{ position: 'relative' }}>
                                        <IconButton
                                            onClick={handleMenuClick}
                                            size="small"
                                            sx={{ margin: 0, marginTop: 2, color: 'grey.500' }}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                        {Boolean(anchorEl) && (
                                            <Box
                                                className="app-card-action"
                                                sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    right: 0,
                                                    zIndex: 1000,
                                                    minWidth: 120,
                                                    backgroundColor: isDark ? '#323130' : '#fafafa',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                                                    borderRadius: 1
                                                }}
                                            >
                                                {onRefresh && (
                                                    <Box
                                                        onClick={handleRefresh}
                                                        sx={{
                                                            height: '40px',
                                                            fontSize: '0.875rem',
                                                            margin: '0.3125rem',
                                                            backgroundColor: isDark ? '#323130' : 'white',
                                                            boxShadow: '0 3px 2px 0 rgba(0, 0, 0, 0.02)',
                                                            transition: 'all 0.4s ease-in-out',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0 12px',
                                                            cursor: 'pointer',
                                                            color: isDark ? '#fff' : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: isDark
                                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                                    : 'white',
                                                                color: 'var(--primary) !important'
                                                            }
                                                        }}
                                                    >
                                                        <Refresh
                                                            fontSize="small"
                                                            sx={{ marginRight: '8px', minWidth: '24px' }}
                                                        />
                                                        {t('refresh')}
                                                    </Box>
                                                )}
                                                {customActions?.map((action, index) => (
                                                    <Box
                                                        key={index}
                                                        onClick={() => handleMenuItemClick(action.onClick)}
                                                        sx={{
                                                            height: '40px',
                                                            fontSize: '0.875rem',
                                                            margin: '0.3125rem',
                                                            backgroundColor: isDark ? '#323130' : 'white',
                                                            boxShadow: '0 3px 2px 0 rgba(0, 0, 0, 0.02)',
                                                            transition: 'all 0.4s ease-in-out',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0 12px',
                                                            cursor: 'pointer',
                                                            color: isDark ? '#fff' : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: isDark
                                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                                    : 'white',
                                                                color: 'var(--primary) !important'
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                marginRight: '8px',
                                                                minWidth: '24px',
                                                                display: 'flex'
                                                            }}
                                                        >
                                                            {action.icon}
                                                        </Box>
                                                        {action.text}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </ClickAwayListener>
                            </Box>
                        )
                    }
                    sx={{
                        minHeight: 68,
                        padding: '0.25rem 1rem',
                        borderBottom: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                />
            )}

            <CardContent
                className="app-card-content"
                sx={{
                    flex: '1 0 auto',
                    position: 'relative',
                    padding: '1rem 1rem'
                }}
            >
                {children}

                {loading && (
                    <Box
                        className="loader-overlay"
                        sx={{
                            position: 'absolute',
                            background: 'rgba(255, 255, 255, 0.8)',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            margin: '0 auto',
                            zIndex: 100
                        }}
                    >
                        <CircularProgress color="primary" />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
